import { Component, EventEmitter, Input, OnInit, Output, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DonationCardService } from '../../../services/donation-card.service';
import { DonationCardGeneralInfoVM, DropdownOption, EditDonationGeneralInfoViewModel, DonationGeneralInfoLookupViewModel } from '../../../models/DonationCardModels';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { CalendarModule } from 'primeng/calendar';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { HttpClient } from '@angular/common/http';
import { SkeletonModule } from 'primeng/skeleton';
import { restrictToTwoDecimals } from '../../../../global/util/util';

@Component({
  selector: 'app-donation-general-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule, 
    DialogModule,
    CalendarModule,
    CardModule,
    InputSwitchModule,
    DropdownModule,
    AutoCompleteModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    SkeletonModule
  ],
  providers: [MessageService],
  templateUrl: './donation-general-tab.component.html',
  styleUrls: ['./donation-general-tab.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DonationGeneralTabComponent implements OnInit {
  @Input() donationId!: number;
  @Input() campaignId!: number;
  @Input() splitId!: number;
  @Input() isReadOnlyMode: boolean = false; // Add the isReadOnlyMode flag
  @Output() updateDonationHeader = new EventEmitter<{ donationDate: string, donationAmount: number, fundCode: string }>();
  @Output() generalInfoUpdated = new EventEmitter<DonationCardGeneralInfoVM>();
  generalInfo: any = null;

  isEditModalVisible = false;
  isEditDataLoading = false;
  editForm!: FormGroup;
  sourceOptions: DropdownOption[] = []; 
  typeOptions: DropdownOption[] = []; 
  filteredEventCodes: DropdownOption[] = []; 
  filteredFundCodes: DropdownOption[] = [];
  apiRefundAmount: number = 0;

  constructor(private service: DonationCardService, private fb: FormBuilder, private messageService: MessageService, private http: HttpClient) {}

  ngOnInit() {
    this.loadGeneralInfo();
    this.initializeEditForm();
    this.loadDropdownOptions();
  }

  loadGeneralInfo() {
    this.service.getGeneralInfo(this.donationId, this.campaignId).subscribe(data => {
      this.generalInfo = data;
      this.generalInfoUpdated.emit(this.generalInfo);
      if (this.generalInfo.isDntHasRdesOrReAttrSplits) {
        this.editForm.get('donationAmount')?.disable();
      } else {
        this.editForm.get('donationAmount')?.enable();
      }
    });
  }

  initializeEditForm() {
    this.editForm = this.fb.group({
      donationDate: [null, Validators.required],
      conversionDate: [null], 
      donationAmount: [{ value: null }, [Validators.required, Validators.pattern('-?[0-9]+(\\.[0-9]{1,2})?')]], // Allow negative values
      refundAmount: [null, [this.refundAmountValidator.bind(this)]],
      eventCode: [null, Validators.required],
      eventId: [null, Validators.required],
      source: [null, Validators.required],
      type: [null, Validators.required],
      isPledged: [false], 
      fundCode: [null, Validators.required],
      fundCodeId: [null, Validators.required]
    });

    this.editForm.get('isPledged')?.valueChanges.subscribe((isPledged: boolean) => {
      if (isPledged) {
        this.editForm.patchValue({ conversionDate: null });
      }
    });

    this.editForm.get('donationAmount')?.valueChanges.subscribe(() => {
      this.editForm.get('refundAmount')?.updateValueAndValidity();
    });
  }

  refundAmountValidator(control: AbstractControl): ValidationErrors | null {
    let refundAmount = control.value;

    // Filter out non-numeric characters except for dot and minus
    if (typeof refundAmount === 'string') {
      refundAmount = refundAmount.replace(/[^0-9.-]/g, '');
    }

    if (refundAmount == null || refundAmount === '') {
      return null;
    }

    const donationAmountRaw = this.editForm?.get('donationAmount')?.value || 0;
    const donationAmount = typeof donationAmountRaw === 'string'
      ? Number(donationAmountRaw.replace(/[^0-9.-]/g, ''))
      : Number(donationAmountRaw);

    if (Number(refundAmount) > donationAmount) {
      return { refundExceedsDonation: true };
    }

    if (Number(refundAmount) < this.apiRefundAmount) {
      return { refundBelowApiAmount: true };
    }

    return null;
  }

  loadDropdownOptions() {
    this.service.getDonationGeneralInfoLookups(this.campaignId).subscribe((data: DonationGeneralInfoLookupViewModel) => {
      this.sourceOptions = [{ text: 'Please Select', value: null }, ...data.donorSources.map(item => ({ text: item.text, value: Number(item.value) }))];
      this.typeOptions = [{ text: 'Please Select', value: null }, ...data.contributionTypes.map(item => ({ text: item.text, value: Number(item.value) }))];
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.editForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  filterEventCodes(event: any) {
    const query = event.query;
    this.service.searchEventCodes(query, this.campaignId).subscribe((data: DropdownOption[]) => {
      this.filteredEventCodes = data.map(item => ({ text: item.text, value: item.value }));
    });
  }

  filterFundCodes(event: any) {
    const query = event.query;
    this.service.searchFundCodes(query, this.splitId, this.campaignId).subscribe((data: DropdownOption[]) => {
      this.filteredFundCodes = data.map(item => ({ text: item.text, value: item.value }));
    });
  }

  onEventCodeSelect(event: AutoCompleteSelectEvent) {
    const selectedOption = event.value;
    this.editForm.patchValue({
      eventCode: selectedOption.text,
      eventId: selectedOption.value
    });
  }

  onFundCodeSelect(event: AutoCompleteSelectEvent) {
    const selectedOption = event.value;
    this.editForm.patchValue({
      fundCode: selectedOption.text,
      fundCodeId: selectedOption.value // Bind fundCodeId
    });
  }

  onEditClick() {
    this.isEditDataLoading = true;
    this.fetchRefundAmountByDonationId(); 

    if (this.generalInfo) {
      this.editForm.patchValue({
        donationDate: this.generalInfo.donationDate ? new Date(this.generalInfo.donationDate) : null,
        conversionDate: this.generalInfo.conversionDate ? new Date(this.generalInfo.conversionDate) : null,
        donationAmount: this.generalInfo.donationAmount,
        refundAmount: this.generalInfo.refundAmount != 0 ? this.generalInfo.refundAmount : '',
        eventCode: this.generalInfo.eventId !== null ? this.generalInfo.eventCode: '',
        eventId: this.generalInfo.eventId,
        source: this.generalInfo.sourceId,
        type: this.generalInfo.contributionTypeId,
        isPledged: this.generalInfo.isPledged,
        fundCode: this.generalInfo.fundCode, 
        fundCodeId: this.generalInfo.fundCodeId 
      });
      this.isEditDataLoading = false;
      this.isEditModalVisible = true;
    }
  }

  private fetchRefundAmountByDonationId(): void {
    this.service.getRefundAmountByDonationId(this.donationId).subscribe({
      next: (response: any) => {
        this.apiRefundAmount = response.refundAmount || 0;
      },
      error: (err) => {
        console.error('Error fetching refund amount:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch refund amount. Please try again later.'
        });
      }
    });
  }

  onEditSubmit() {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.editForm.valid) {
      const donationAmountControl = this.editForm.get('donationAmount');
      const donationAmount = donationAmountControl?.enabled ? donationAmountControl.value || 0 : this.generalInfo?.donationAmount || 0;
      const updatedInfo: EditDonationGeneralInfoViewModel = {
        donationId: this.donationId,
        splitId: this.splitId,
        donationDate: this.editForm.value.donationDate,
        conversionDate: this.editForm.value.conversionDate,
        donationAmount: donationAmount,
        refundAmount: this.editForm.value.refundAmount === '' ? 0 : this.editForm.value.refundAmount,
        eventId: this.editForm.value.eventCode !== 'N/A' ? this.editForm.value.eventId : null,
        contributionSourceId: this.editForm.value.source,
        contributionTypeId: this.editForm.value.type,
        pledged: this.editForm.value.isPledged,
        fundCode: this.editForm.value.fundCode, 
        fundCodeId: this.editForm.value.fundCodeId 
      };

      this.service.updateGeneralInfo(updatedInfo).subscribe({
        next: () => {
          this.isEditModalVisible = false;
          this.service.getGeneralInfo(this.donationId, this.campaignId).subscribe(data => this.generalInfo = data);
          this.messageService.add({ severity: 'success', summary: 'Update Successful', detail: 'General info updated successfully.' });
          window.location.reload();
          //this.updateDonationHeader.emit({
          //  donationDate: updatedInfo.donationDate ? updatedInfo.donationDate.toISOString().split('T')[0] : '',
          //  donationAmount: updatedInfo.donationAmount,
          //  fundCode: updatedInfo.fundCode || ''
          //});
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Update Failed', detail: 'An error occurred while updating general info.' });
        }
      });
    } else {
      this.editForm.markAllAsTouched();
      //if (this.editForm.hasError('refundExceedsDonation', 'refundAmount')) {
      //  this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Refund amount cannot exceed donation amount.' });
      //}
    }
  }

  onEditCancel() {
    this.isEditModalVisible = false;
  }

  onAmountInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const formattedValue = restrictToTwoDecimals(input.value);
    input.value = formattedValue;
    this.editForm.get(controlName)?.setValue(formattedValue, { emitEvent: false });
  }
}
